import MobileReady from '@/pages/auth/mobile-ready';
import { Switch, Route, useLocation } from "wouter";
import EvidenceMonitor from './pages/evidence-monitor';
import { useQueryClient } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";
// import { TooltipProvider } from "@/components/ui/tooltip";
import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import LogRocket from 'logrocket';
import { dbg } from '@/lib/dbg';
import { cn } from "@/lib/utils";
import { androidClass } from "@/lib/android-theme";

// Initialize LogRocket for production debugging
if (import.meta.env.PROD) {
  LogRocket.init('hrywkt/billposter', {
    release: 'v1.0.0',
    network: { isEnabled: true },
    console: { isEnabled: true },
  });
  dbg.log("✅ LogRocket active");
}
import Sidebar from "@/components/sidebar";
import LandingRouter from "@/components/LandingRouter";
import { PortalRoutes } from "@/pages/portal/PortalRoutes";
import FieldDashboard from "@/pages/field-dashboard";
import { OfflineStatusBar } from "@/components/OfflineStatusBar";
import { OperatorErrorBoundary } from "@/components/OperatorErrorBoundary";
import { ErrorBoundary } from "@/components/ErrorBoundary";

import RetailerManagement from "@/pages/retailer-management";
import CampaignUpload from "@/pages/campaign-upload";
import CampaignUploadPacks from "@/pages/campaign-upload-packs";
import CyclesDashboardV2 from "@/pages/cycles-dashboard-v2";
import CampaignsPage from "@/pages/campaigns";
import ComponentDemos from "@/pages/component-demos";

import UserManagement from "@/pages/user-management";
import BookingEngine from "@/pages/booking-engine";
import PackManagement from "@/pages/pack-management";
import { PackLockingDashboard } from "@/components/pack-locking";
import OperatorDashboard from "@/pages/operator-dashboard";
import RouteOverview from "@/pages/route-overview";
import CycleLanding from "@/pages/cycle-landing";
import CycleCompletePage from "@/pages/cycle-complete";
import StoreDetailPage from "@/pages/store-detail";
import CycleSelector from "@/pages/mobile/CycleSelector";
import AdminProscriptivePage from "@/pages/admin/proscriptive/[cycleId]";
import AuthCallback from "@/pages/auth-callback";
import AuthVerify from "@/pages/auth/verify";
import AuthError from "@/pages/auth/error";
import SetPassword from "@/pages/set-password";
import Onboarding from "@/pages/onboarding";
import OperatorCycleReports from "@/pages/operator-management-clean";
import OperatorCycleReportsStandalone from "@/pages/operator-cycle-reports";
import CampaignStatusManagement from "@/pages/campaign-status-management-simple";
import ImageGallery from "@/pages/image-gallery";
import ChangePassword from "@/pages/change-password";

import ComprehensiveRemuneration from "@/pages/comprehensive-remuneration";

import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import OperatorTasks from "@/pages/operator-tasks";

import CampaignCreation from "@/pages/campaign-creation";
import CampaignDetails from "@/pages/campaign-details";
import CampaignEditor from "@/pages/campaign-editor";
import CampaignRebuild from "@/pages/campaign-rebuild";
import RestructurePrototype from "@/pages/restructure-prototype";
import CyclesPrototype from "@/pages/cycles-prototype";
import TaskDetail from "@/pages/task-detail";
import AssignmentManagement from "@/pages/assignment-management";

import SignOffV2 from "@/pages/sign-off-v2";
import ProofOfPostingV2Enhanced from "@/pages/proof-of-posting-v2-enhanced";

import CycleSnapshotsListPage from "@/pages/cycle-snapshots-list";
import CycleSnapshotViewerPage from "@/pages/cycle-snapshot-viewer";
import LegacyProofOfPostingRedirect from "@/components/legacy-proof-of-posting-redirect";
import BulkStoreUpload from "@/pages/bulk-store-upload";
import NotFound from "@/pages/not-found";
import { Menu, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { User } from "@shared/schema";
import DatabaseSchema from "./pages/database-schema";
import SuperAdminBusinessRules from "@/pages/super-admin-business-rules";
import SuperAdminManagement from "@/pages/superadmin-management";
import SuperAdminLookAndFeelPage from "@/pages/superadmin/look-and-feel";
import StoreProfilesListingPage from "@/pages/superadmin/store-profiles";
import StoreProfileDetailPage from "@/pages/superadmin/store-profile-detail";

import { useGlobalTheme, applyFullGlobalTheme } from "@/hooks/useGlobalTheme";
import SignatureTest from "@/pages/signature-test";
import { useToast } from "@/hooks/use-toast";
import { useMutation } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth-store";
import { useAuthUser } from "@/hooks/useAuthUser";
import { signOut } from "@/lib/authBootstrap";
import StatusPage from "@/pages/Status";
// import { TopNotificationBell } from "@/components/TopNotificationBell"; // Commented out - endpoint not implemented
// Legacy debug login removed (Supabase handles auth)
import PackLockingSelector from "@/pages/pack-locking-selector";
import { User as AuthUser } from "@/lib/auth-store";
import { ProtectedRoute, PublicRoute } from "@/components/ProtectedRoute";
import DevEnvChip from "@/components/DevEnvChip";
// CAPACITOR REMOVED: useNetworkSync, useBackgroundUploader, useAndroidBackButton, usePushNotifications
// Mobile app is now handled by billposter-expo
import { useUploadQueue } from "@/hooks/useUploadQueue";
import { useTransitionQueue } from "@/hooks/useTransitionQueue";
import { useOfflineSync } from "@/hooks/useOfflineSync";
import { MobileScaffold } from "@/components/mobile/MobileScaffold";
// Admin debug/minimal pages
import AdminAuthDebugPage from "@/pages/admin/auth";
import AIVerificationAdminPage from "@/pages/admin/ai/verification";
import AIGoldCandidatesPage from "@/pages/admin/ai/gold-candidates";
import AIEvalHarnessPage from "@/pages/admin/ai/eval-harness";
import PhotoChecksPage from "@/pages/admin/ai/checks";
import PhotoChecksResultsPage from "@/pages/admin/ai/results";
import PhotoChecksAdvancedPage from "@/pages/admin/ai/advanced";
import InstoreLocationsAdminPage from "@/pages/admin/instore-locations";
import AdminOperatorTasksV2 from "@/pages/admin-operator-tasks-v2";
import AdminOperatorPaymentReports from "@/pages/admin-operator-payment-reports";
import AdminOperatorReassignment from "@/pages/admin/operator-reassignment";
import AdminRemovalsPreviewPage from "@/pages/admin/removals-preview";
import AdminPostingsPage from "@/pages/admin/postings";
import AdminAuditsPage from "@/pages/admin/audits";
import TaskManagement from "@/pages/task-management";
import CycleSnapshotsPage from "@/pages/admin/cycle-snapshots";
import RetailerInstoreLocationsPage from "@/pages/admin/retailers/[retailerId]/in-store-locations";
// import ThemeSettingsPage from "@/pages/admin/theme-settings"; // REMOVED: Personal themes disabled, use SuperAdmin Look & Feel instead
import AdminAdvertisersPage from "@/pages/admin-advertisers";
import AdminClientsPage from "@/pages/admin-clients";
import PartnerAccessPage from "@/pages/admin/partner-access";
import PartnerAccessOrgDetailPage from "@/pages/admin/partner-access/org-detail";
import AdminBookingsPage from "@/pages/admin-bookings";
import ClientDetailPage from "@/pages/admin-clients/client-detail";
import AdvertiserDetailPage from "@/pages/admin-advertisers/advertiser-detail";
import BookingDetailPage from "@/pages/admin-bookings/booking-detail";
import AdminLogsPage from "@/pages/admin-logs";
import AITriagePromptStudioPage from "@/pages/admin-ai-triage-prompts";
import AdminAIPage from "@/pages/admin-ai";
// CameraTest removed
import TableDemo from "@/pages/table-demo";
// Account Management (unified Clients + Advertisers)
import AccountsPage from "@/pages/accounts";
import AccountDetailPage from "@/pages/accounts/account-detail";
// Tracker (Field Ops Mission Control)
import {
  TrackerDashboard,
  ProgressView,
  OperatorRouteView as TrackerOperatorRouteView,
  CampaignTracker,
  StoreTracker,
  TrackerReviewTab,
  TrackerByOperator,
  RejectionReasonSettingsTab
} from '@/pages/tracker';
import OfficeEmergencyCopiesPage from '@/pages/admin/OfficeEmergencyCopiesPage';
import AdministrativeStoreRecoveryPage from '@/pages/admin/AdministrativeStoreRecoveryPage';
import SubstitutePostingPage from '@/pages/admin/SubstitutePostingPage';
// Branded loading screen shown while auth state is being determined.
// Uses the same nav background + logo as the login page for a seamless transition.
// Logo URL is persisted in sessionStorage so it's available instantly on reload/logout.
const LOGO_CACHE_KEY = 'reactor_theme_logo';

function BrandedLoadingScreen() {
  const [logoUrl, setLogoUrl] = React.useState<string | null>(() => {
    try { return sessionStorage.getItem(LOGO_CACHE_KEY); } catch { return null; }
  });

  React.useEffect(() => {
    fetch('/api/look-and-feel/public')
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        const url = d?.logoUrl ?? null;
        setLogoUrl(url);
        try {
          if (url) sessionStorage.setItem(LOGO_CACHE_KEY, url);
          else sessionStorage.removeItem(LOGO_CACHE_KEY);
        } catch { }
      })
      .catch(() => {});
  }, []);

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center gap-6"
      style={{ backgroundColor: 'var(--navigation-bg, #0F3A3F)' }}
    >
      {logoUrl ? (
        <img src={logoUrl} alt="Logo" className="h-10 max-w-[200px] object-contain opacity-90" />
      ) : (
        <span className="text-white text-2xl font-semibold opacity-90">ReActor</span>
      )}
      <div className="flex gap-1.5">
        <span className="w-2 h-2 rounded-full bg-white/70 animate-bounce" style={{ animationDelay: '0ms' }} />
        <span className="w-2 h-2 rounded-full bg-white/70 animate-bounce" style={{ animationDelay: '150ms' }} />
        <span className="w-2 h-2 rounded-full bg-white/70 animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  );
}

// Operator Route Guard Component  
function OperatorRouteGuard({ children }: { children: React.ReactNode }) {
  const { state } = useAuth();

  const roles = (state.user?.roles as string[]) || [];
  const role = state.user?.role || null;
  const isAdmin = role === 'admin' || role === 'super_admin' || role === 'superadmin'
    || (Array.isArray(roles) && (roles.includes('admin') || roles.includes('super_admin') || roles.includes('superadmin')));
  const isOperator = role === 'operator' || (Array.isArray(roles) && roles.includes('operator'));

  // Temporarily do not redirect here; routing below decides destination based on roles

  return <>{children}</>;
}

function Router() {
  console.log('[ROUTER] Router component rendering');
  const { state } = useAuth();
  const queryClient = useQueryClient();
  const [currentPath, setLocation] = useLocation();

  // ─────────────────────────────────────────────────────────────────────────────
  // LOAD AND APPLY GLOBAL THEME (SuperAdmin-Controlled Brand Colors)
  // ─────────────────────────────────────────────────────────────────────────────
  // This runs BEFORE the personal ThemeProvider loads, ensuring global brand colors
  // are applied first and never overridden by per-user preferences.
  // 
  // Order of operations:
  // 1. Global theme loads (--global-primary, --global-accent)
  // 2. Personal theme provider loads (dark/light, fonts, customBrandColor)
  // 3. Personal prefs override their own CSS variables, NOT global ones
  // ─────────────────────────────────────────────────────────────────────────────
  const globalThemeQuery = useGlobalTheme();

  useEffect(() => {
    if (globalThemeQuery.data && state.user) {
      const roles = (state.user.roles as string[]) || [];
      const role = state.user.role || null;
      const isAdmin = role === 'admin' || role === 'super_admin' || (Array.isArray(roles) && (roles.includes('admin') || roles.includes('super_admin')));

      // Apply full global theme (colors, nav, fonts) via CSS variables
      // Sidebar, buttons, and other UI elements reference these variables
      if (globalThemeQuery.data) {
        applyFullGlobalTheme(globalThemeQuery.data);
      }
    }
  }, [globalThemeQuery.data, state.user]);

  // Force refetch of global theme when user becomes available (fixes race condition)
  useEffect(() => {
    if (state.user) {
      queryClient.invalidateQueries({ queryKey: ['global-theme'] });
    }
  }, [state.user, queryClient]);

  // CAPACITOR REMOVED: useAndroidBackButton and usePushNotifications
  // Mobile app is now handled by billposter-expo

  const isAuthenticated = state.status === 'authenticated';
  const isLoading = state.status === 'unknown';

  // Handle password reset routes BEFORE authentication checks
  if (currentPath === '/forgot-password'
    || currentPath === '/reset-password'
    || currentPath === '/set-password'
    || currentPath === '/onboarding'
    || currentPath === '/auth/verify') {
    console.log('[ROUTER] Handling password reset/verify/onboarding route:', currentPath);
    if (isLoading) {
      return <BrandedLoadingScreen />;
    }
    if (currentPath === '/forgot-password') return <ForgotPasswordPage />;
    if (currentPath === '/set-password') return <SetPassword />;
    if (currentPath === '/onboarding') return <Onboarding />;
    if (currentPath === '/auth/verify') return <AuthVerify />;
    return <ResetPasswordPage />;
  }

  console.log('[ROUTER] Auth state:', { user: !!state.user, status: state.status, isAuthenticated, isLoading });
  // Use session from our centralized auth system

  // Guaranteed-visible status route even if auth/router is broken
  if (currentPath === '/status' || currentPath === '/__status') {
    return <StatusPage />;
  }

  // Initialize LogRocket user identification when user data is available
  if (import.meta.env.PROD && state.user?.id) {
    LogRocket.identify(state.user.id.toString(), {
      name: state.user.username || 'Unknown',
      email: state.user.email || 'N/A',
      role: state.user.role || 'unknown',
    });
  }

  // Handle login success
  const handleLoginSuccess = (_loggedInUser: AuthUser, _requirePasswordChange = false) => {
    // Supabase manages password state; no forced password change flow
  };

  // Redirect to login if not authenticated (in useEffect to avoid setState during render)
  useEffect(() => {
    if (!isLoading && !isAuthenticated && currentPath !== '/login') {
      setLocation('/login');
    }
  }, [isLoading, isAuthenticated, currentPath, setLocation]);

  // Show login page if not authenticated
  if (!isLoading && !isAuthenticated) {
    return (
      <PublicRoute>
        <LoginPage onLoginSuccess={handleLoginSuccess} />
      </PublicRoute>
    );
  }

  // Show loading while checking authentication
  if (isLoading) {
    return <BrandedLoadingScreen />;
  }

  // Unified role detection (prioritize admin/super_admin over operator)
  const roles = (state.user?.roles as string[]) || [];
  const role = state.user?.role || null;
  // Check both underscore and no-underscore variants for superadmin
  const isAdmin = role === 'admin' || role === 'super_admin' || role === 'superadmin' || (Array.isArray(roles) && (roles.includes('admin') || roles.includes('super_admin') || roles.includes('superadmin')));
  const isReviewer = role === 'reviewer' || (Array.isArray(roles) && roles.includes('reviewer'));
  const isOperator = role === 'operator' || (Array.isArray(roles) && roles.includes('operator'));
  const isPortalExternal = (state.user as { accessMode?: string })?.accessMode === 'portal_external';

  // DETAILED debug logging for authentication state
  console.log("[AUTH_DEBUG] Router - DETAILED role check:", {
    user: !!state.user,
    role,
    roles,
    isAdmin,
    isReviewer,
    isOperator,
    isPortalExternal,
    willShowAdminRoutes: isAdmin || isReviewer,
    currentPath: window.location.pathname
  });

  if (isPortalExternal) {
    return (
      <ProtectedRoute>
        <Switch>
          <Route path="/" component={LandingRouter} />
          <Route path="/portal/campaigns/:campaignId/gallery" component={PortalRoutes} />
          <Route path="/portal/gallery" component={PortalRoutes} />
          <Route path="/portal/campaigns/:campaignId" component={PortalRoutes} />
          <Route path="/portal/compliance" component={PortalRoutes} />
          <Route path="/portal/cockpit" component={PortalRoutes} />
          <Route path="/portal/cycles" component={PortalRoutes} />
          <Route path="/portal/stores" component={PortalRoutes} />
          <Route path="/portal" component={PortalRoutes} />
          <Route component={() => {
            window.location.href = '/portal';
            return null;
          }} />
        </Switch>
      </ProtectedRoute>
    );
  }

  // DIAGNOSTIC: Log when route check fails for cockpit review
  if (currentPath === '/admin/activation-cockpit/review' && !(isAdmin || isReviewer)) {
    console.error('[ROUTE_DIAGNOSTIC] cockpit review access DENIED - role check failed:', {
      role,
      roles,
      isAdmin,
      isReviewer,
      user: state.user?.email || state.user?.username
    });
  }

  if (isAdmin || isReviewer) {
    return (
      <ProtectedRoute>
        <Switch>
          {/* Smart landing: decide where to go based on role */}
          <Route path="/" component={LandingRouter} />
          {/* Admin default landing */}
          <Route
            path="/campaigns"
            component={() => (
              <ErrorBoundary>
                <CampaignsPage />
              </ErrorBoundary>
            )}
          />
          {/* Component showcase */}
          <Route path="/component-demos" component={ComponentDemos} />
          {/* Account Management (unified Clients + Advertisers) */}
          <Route path="/accounts" component={AccountsPage} />
          <Route path="/accounts/:id" component={AccountDetailPage} />
          {/* Admin routes */}
          <Route path="/cycles" component={CyclesDashboardV2} />
          <Route path="/cycle/:cycleId/complete" component={CycleCompletePage} />
          <Route path="/retailer-management/:retailerId/:section" component={RetailerManagement} />
          <Route path="/retailer-management/:retailerId" component={RetailerManagement} />
          <Route path="/retailer-management" component={RetailerManagement} />
          <Route path="/advertisers" component={AdminAdvertisersPage} />
          <Route path="/table-demo" component={TableDemo} />
          <Route path="/upload" component={CampaignUpload} />
          <Route path="/campaign-upload" component={CampaignUpload} />
          <Route path="/campaign-upload-packs" component={CampaignUploadPacks} />
          <Route path="/create-campaign" component={CampaignCreation} />
          <Route path="/restructure-prototype" component={RestructurePrototype} />
          <Route path="/cycles-prototype" component={CyclesPrototype} />
          <Route path="/campaigns/:id" component={CampaignDetails} />
          <Route path="/campaigns/:id/rebuild" component={CampaignRebuild} />
          <Route path="/campaign-details/:id" component={CampaignDetails} />
          {/* Redirect old edit route to campaign details */}
          <Route path="/campaigns/:id/edit">
            {(params) => {
              window.location.href = `/campaigns/${params.id}?edit=true`;
              return (
                <div className="min-h-screen flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Redirecting to campaign details...</p>
                  </div>
                </div>
              );
            }}
          </Route>
          <Route path="/tasks/assignments" component={AssignmentManagement} />
          <Route path="/tasks/:taskId" component={TaskDetail} />
          <Route path="/proof-of-posting" component={LegacyProofOfPostingRedirect} />
          <Route path="/proof-of-posting-v2/:cycleId">
            {(params) => {
              window.location.href = `/proof-of-posting-enhanced?cycleId=${params.cycleId}`;
              return (
                <div className="min-h-screen flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Redirecting...</p>
                  </div>
                </div>
              );
            }}
          </Route>
          <Route path="/proof-of-posting-v2">
            {() => {
              window.location.href = `/proof-of-posting-enhanced`;
              return (
                <div className="min-h-screen flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Redirecting...</p>
                  </div>
                </div>
              );
            }}
          </Route>
          <Route path="/proof-of-posting-enhanced/compliance">
            {() => <ProofOfPostingV2Enhanced initialSection="compliance" />}
          </Route>
          <Route path="/proof-of-posting-enhanced/reviews">
            {() => <ProofOfPostingV2Enhanced initialSection="reviews" />}
          </Route>
          <Route path="/proof-of-posting-enhanced">
            {() => <ProofOfPostingV2Enhanced />}
          </Route>
          <Route path="/sign-off-v2/:cycleId">
            {(params) => <SignOffV2 cycleId={params.cycleId} />}
          </Route>
          <Route path="/sign-off-v2">{() => <SignOffV2 />}</Route>
          {/* V3 routes removed */}
          {/* New campaign snapshots routes */}
          <Route path="/cycles/:cycleId/campaign-snapshots" component={CycleSnapshotsListPage} />
          <Route path="/cycles/:cycleId/campaign-snapshots/:id" component={CycleSnapshotViewerPage} />
          {/* Legacy redirects for old snapshot URLs */}
          <Route path="/cycles/:cycleId/snapshots">
            {(params) => {
              window.location.href = `/cycles/${params.cycleId}/campaign-snapshots`;
              return null;
            }}
          </Route>
          <Route path="/cycles/:cycleId/snapshots/:id">
            {(params) => {
              window.location.href = `/cycles/${params.cycleId}/campaign-snapshots/${params.id}`;
              return null;
            }}
          </Route>
          <Route path="/__status" component={StatusPage} />
          <Route path="/status" component={StatusPage} />

          <Route path="/booking" component={BookingEngine} />
          {/* <Route path="/pack-management" component={PackManagement} /> */}
          {/* MOTHBALLED: Use retailer-management for pack editing instead */}
          <Route path="/pack-locking" component={PackLockingSelector} />
          <Route path="/pack-locking/:packId" component={PackLockingDashboard} />
          <Route path="/bulk-store-upload" component={BulkStoreUpload} />

          <Route
            path="/users"
            component={() => (
              <ErrorBoundary>
                <UserManagement />
              </ErrorBoundary>
            )}
          />
          <Route path="/remuneration">
            {() => {
              window.location.href = "/admin/operator-tasks";
              return null;
            }}
          </Route>
          <Route
            path="/comprehensive-remuneration"
            component={() => (
              <ErrorBoundary>
                <ComprehensiveRemuneration />
              </ErrorBoundary>
            )}
          />
          <Route path="/operator-cycle-reports" component={OperatorCycleReports} />
          <Route path="/operator-cycle-reports-standalone" component={OperatorCycleReportsStandalone} />

          <Route path="/campaign-status" component={CampaignStatusManagement} />
          <Route path="/image-gallery" component={ImageGallery} />
          <Route path="/change-password" component={ChangePassword} />
          <Route path="/database-schema" component={DatabaseSchema} />
          <Route path="/evidence-monitor" component={EvidenceMonitor} />

          {/* Minimal Admin debug pages */}
          <Route path="/admin/auth" component={AdminAuthDebugPage} />
          {/* <Route path="/admin/theme-settings" component={ThemeSettingsPage} /> */}
          {/* REMOVED: Personal theme settings - use SuperAdmin Look & Feel instead */}
          <Route path="/admin/store-formats">
            {() => {
              window.location.href = "/retailer-management";
              return (
                <div className="min-h-screen flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Redirecting to Retailer Media Formats...</p>
                  </div>
                </div>
              );
            }}
          </Route>
          <Route path="/admin/instore-locations" component={InstoreLocationsAdminPage} />
          {/* /admin/snapshots → CycleSnapshotsPage (client/src/pages/admin/cycle-snapshots/index.tsx) */}
          <Route path="/admin/snapshots" component={CycleSnapshotsPage} />
          <Route path="/admin/cycle-snapshots" component={CycleSnapshotsPage} />
          <Route path="/admin/proscriptive/:cycleId" component={AdminProscriptivePage} />
          <Route path="/admin/retailers/:retailerId/in-store-locations">
            {(params) => <RetailerInstoreLocationsPage retailerIdOverride={params.retailerId ? Number(params.retailerId) : undefined} />}
          </Route>

          <Route path="/admin/advertisers" component={AdminAdvertisersPage} />
          <Route path="/admin/advertisers/:id" component={AdvertiserDetailPage} />

          {/* Client-Led Model Phase 2A: Client Management */}
          <Route path="/admin/clients" component={AdminClientsPage} />
          <Route path="/admin/bookings" component={AdminBookingsPage} />
          <Route path="/admin/bookings/:id" component={BookingDetailPage} />
          <Route path="/admin/clients/:id" component={ClientDetailPage} />
          <Route path="/admin/partner-access" component={PartnerAccessPage} />
          <Route path="/admin/partner-access/:organisationId" component={PartnerAccessOrgDetailPage} />

          {/* Operator Tasks & Payment Reports */}
          <Route path="/admin/task-templates" component={TaskManagement} />
          <Route path="/admin/operator-tasks" component={AdminOperatorTasksV2} />
          <Route path="/admin/payment-reports" component={AdminOperatorPaymentReports} />
          <Route path="/admin/operator-payment-reports" component={AdminOperatorPaymentReports} />
          <Route path="/admin/operator-reassignment" component={AdminOperatorReassignment} />
          <Route path="/admin/removals-preview" component={AdminRemovalsPreviewPage} />
          <Route path="/admin/postings" component={AdminPostingsPage} />
          <Route path="/admin/audits/templates" component={AdminAuditsPage} />
          <Route path="/admin/audits/new" component={AdminAuditsPage} />
          <Route path="/admin/audits/:auditId" component={AdminAuditsPage} />
          <Route path="/admin/audits" component={AdminAuditsPage} />

          {/* Tracker - Field Ops Mission Control */}
          <Route path="/admin/activation-cockpit/dashboard">
            <TrackerDashboard />
          </Route>
          <Route path="/admin/activation-cockpit/by-operator" component={TrackerByOperator} />
          <Route path="/admin/activation-cockpit/review" component={TrackerReviewTab} />
          <Route path="/admin/activation-cockpit/settings/rejection-reasons" component={RejectionReasonSettingsTab} />
          <Route path="/admin/activation-cockpit/progress/operators">
            <ProgressView />
          </Route>
          <Route path="/admin/activation-cockpit/operator/:operatorId">
            <TrackerOperatorRouteView />
          </Route>
          <Route path="/admin/activation-cockpit/progress/campaigns">
            <CampaignTracker />
          </Route>
          <Route path="/admin/activation-cockpit/campaign/:campaignId">
            <CampaignTracker />
          </Route>
          <Route path="/admin/activation-cockpit/progress/stores">
            <StoreTracker />
          </Route>
          <Route path="/admin/office-emergency-copies" component={OfficeEmergencyCopiesPage} />
          <Route path="/admin/activation-cockpit/recovery/store" component={AdministrativeStoreRecoveryPage} />
          <Route path="/admin/activation-cockpit/substitute-posting/store" component={SubstitutePostingPage} />

          {/* Super Admin only routes - protected by backend middleware */}
          <Route path="/admin/logs" component={AdminLogsPage} />
          <Route path="/admin/ai/gold-candidates" component={AIGoldCandidatesPage} />
          <Route path="/admin/ai/checks" component={PhotoChecksPage} />
          <Route path="/admin/ai/results" component={PhotoChecksResultsPage} />
          <Route path="/admin/ai/advanced" component={PhotoChecksAdvancedPage} />
          <Route path="/admin/ai/eval" component={AIEvalHarnessPage} />
          <Route path="/admin/ai/verification" component={AIVerificationAdminPage} />
          <Route path="/admin/ai" component={AdminAIPage} />
          <Route path="/admin/ai-triage/prompts" component={AITriagePromptStudioPage} />
          <Route path="/admin/ai-labeling">
            {() => {
              window.location.href = "/admin/ai/advanced";
              return (
                <div className="min-h-screen flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Redirecting…</p>
                  </div>
                </div>
              );
            }}
          </Route>
          <Route path="/superadmin-management" component={SuperAdminManagement} />
          <Route path="/super-admin/business-rules" component={SuperAdminBusinessRules} />
          <Route path="/super-admin/look-and-feel">
            {() => {
              const userRoles = (state.user?.roles as string[]) || [];
              const userRole = state.user?.role || null;
              const isSuperAdminUser = userRole === 'superadmin' || userRole === 'super_admin'
                || userRoles.includes('superadmin') || userRoles.includes('super_admin');
              if (!isSuperAdminUser) {
                return (
                  <div className="min-h-screen flex items-center justify-center bg-gray-50">
                    <div className="text-center">
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
                      <p className="text-gray-600">Only Super Admins can access Look &amp; Feel settings.</p>
                    </div>
                  </div>
                );
              }
              return <SuperAdminLookAndFeelPage />;
            }}
          </Route>
          <Route path="/super-admin/store-profiles/:storeId" component={StoreProfileDetailPage} />
          <Route path="/super-admin/store-profiles" component={StoreProfilesListingPage} />

          {/* Signature Test Page - for debugging */}
          <Route path="/signature-test" component={SignatureTest} />
          {/* CameraTest removed */}

          {/* PDF Preview route removed to avoid confusion; backend HTML remains */}

          <Route component={NotFound} />
        </Switch>
      </ProtectedRoute>
    );
  } else if (isOperator) {
    // Operators only see Field Operations - no admin access
    console.log("[AUTH_DEBUG] OPERATOR ROUTING: User not recognized as admin, showing operator routes for:", {
      username: state.user?.username,
      role
    });
    return (
      <ProtectedRoute>
        <MobileScaffold>
          <Switch>
            {/* Smart landing: decide where to go based on role */}
            <Route path="/" component={LandingRouter} />
            {/* Store-First Mobile Routes - THE ONLY OPERATOR UI */}
            <Route path="/mobile/cycle-selector" component={CycleSelector} />
            <Route path="/cycle/:cycleId/landing" component={CycleLanding} />
            <Route path="/cycle/:cycleId/complete" component={CycleCompletePage} />
            <Route path="/mobile/route-overview/:operatorId/:cycleId" component={RouteOverview} />
            <Route path="/route-overview/:cycleId?" component={RouteOverview} />
            <Route path="/store/:storeId" component={StoreDetailPage} />
            <Route path="/change-password" component={ChangePassword} />
            <Route path="/proof-of-posting-enhanced/compliance">
              {() => <ProofOfPostingV2Enhanced initialSection="compliance" />}
            </Route>
            <Route path="/proof-of-posting-enhanced/reviews">
              {() => <ProofOfPostingV2Enhanced initialSection="reviews" />}
            </Route>
            <Route path="/proof-of-posting-enhanced">
              {() => <ProofOfPostingV2Enhanced />}
            </Route>
            <Route path="/sign-off-v2/:cycleId">
              {(params) => <SignOffV2 cycleId={params.cycleId} />}
            </Route>
            <Route path="/sign-off-v2">{() => <SignOffV2 />}</Route>
            {/* SignOffV3 routes removed - component doesn't exist */}
            <Route path="/image-gallery" component={ImageGallery} />
            {/* PDF view should be accessible to all users */}
            {/* Old V2 routes redirect to enhanced version */}
            <Route path="/proof-of-posting-v2/:cycleId">
              {(params) => {
                window.location.href = `/proof-of-posting-enhanced?cycleId=${params.cycleId}`;
                return (
                  <div className="min-h-screen flex items-center justify-center">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="mt-2 text-gray-600">Redirecting...</p>
                    </div>
                  </div>
                );
              }}
            </Route>
            <Route path="/proof-of-posting-v2">
              {() => {
                window.location.href = `/proof-of-posting-enhanced`;
                return (
                  <div className="min-h-screen flex items-center justify-center">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="mt-2 text-gray-600">Redirecting...</p>
                    </div>
                  </div>
                );
              }}
            </Route>
            {/* All other routes redirect to route overview */}
            <Route component={() => {
              // Redirect any other route to route overview (store-first UI)
              window.location.href = '/route-overview';
              return (
                <div className="min-h-screen flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Redirecting...</p>
                  </div>
                </div>
              );
            }} />
          </Switch>
        </MobileScaffold>
      </ProtectedRoute>
    );
  } else {
    // Unknown role: show login page via PublicRoute to avoid recursion
    return (
      <PublicRoute>
        <LoginPage onLoginSuccess={() => { /* no-op, will re-bootstrap */ }} />
      </PublicRoute>
    );
  }
}

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // CAPACITOR REMOVED: useNetworkSync and useBackgroundUploader
  // Mobile app is now handled by billposter-expo
  // Web offline support uses useUploadQueue and useTransitionQueue directly
  const { processQueue } = useUploadQueue();
  const { processQueue: processTransitionQueue } = useTransitionQueue();

  // Process queues on page visibility change (web-native approach)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        processQueue();
        processTransitionQueue();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [processQueue, processTransitionQueue]);

  // [OFFLINE_MUTATION_QUEUE] Enable offline mutation queuing and sync
  useOfflineSync();

  // Handle all auth and password management routes BEFORE entering authenticated app
  // Use wouter's useLocation so client-side navigations (e.g. login → forgot-password)
  // trigger a re-render and hit the correct early-return path.
  const [path] = useLocation();

  // Compatibility redirect for old invite links that used /auth-callback
  if (path === '/auth-callback') {
    window.location.replace('/auth/callback' + window.location.search + window.location.hash);
    return null;
  }

  if (path === '/auth/mobile-ready') return <MobileReady />;

  if (path === '/auth/callback') {
    return (
      <>
        <AuthCallback />
        {(import.meta as any)?.env?.DEV ? <DevEnvChip /> : null}
      </>
    );
  }

  if (path === '/auth/verify') {
    return (
      <>
        <AuthVerify />
        {(import.meta as any)?.env?.DEV ? <DevEnvChip /> : null}
      </>
    );
  }

  if (path === '/auth/error') {
    return (
      <>
        <AuthError />
        {(import.meta as any)?.env?.DEV ? <DevEnvChip /> : null}
      </>
    );
  }

  if (path === '/forgot-password') {
    return (
      <>
        <ForgotPasswordPage />
        {(import.meta as any)?.env?.DEV ? <DevEnvChip /> : null}
      </>
    );
  }

  if (path === '/reset-password') {
    return (
      <>
        <ResetPasswordPage />
        {(import.meta as any)?.env?.DEV ? <DevEnvChip /> : null}
      </>
    );
  }

  if (path === '/set-password') {
    return (
      <>
        <SetPassword />
        {(import.meta as any)?.env?.DEV ? <DevEnvChip /> : null}
      </>
    );
  }


  return (
    <>
      <div className="bg-background">
        <AuthenticatedApp sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
        {(import.meta as any)?.env?.DEV ? <DevEnvChip /> : null}
      </div>
      <Toaster />
    </>
  );
}

// Authenticated App Component that shows different UI for admin vs operator
// Memoized to prevent unnecessary rerenders when auth state doesn't change
const AuthenticatedApp = React.memo(function AuthenticatedApp({ sidebarOpen, setSidebarOpen }: { sidebarOpen: boolean; setSidebarOpen: (open: boolean) => void }) {
  console.log('[AUTH_APP] AuthenticatedApp rendering');
  const { state, dispatch } = useAuth();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const { user: authUser, isLoading: authUserLoading } = useAuthUser();
  const isLoading = state.status === 'unknown';
  const isAuthenticated = state.status === 'authenticated';
  const currentUserSafe = state.user || null;
  const { toast } = useToast();

  console.log('[AUTH_APP] Auth state:', { hasUser: !!currentUserSafe, isLoading, isAuthenticated });

  // Ensure auth state leaves "unknown" by syncing with /api/users/me
  useEffect(() => {
    if (authUserLoading) return;
    if (authUser) {
      dispatch({ type: 'AUTH_OK', user: authUser });
    } else {
      dispatch({ type: 'AUTH_NONE' });
    }
  }, [authUser, authUserLoading, dispatch]);

  const logoutMutation = useMutation({
    mutationFn: async () => {
      console.log('[AUTH_LOGOUT] AuthenticatedApp: Signing out...');
      await signOut();
    },
    onSuccess: () => {
      console.log('[AUTH_LOGOUT] AuthenticatedApp: Sign out successful');
      // Hard reload to /login — avoids any in-flight React state causing a
      // flash of the wrong route. The server injects fresh theme CSS vars on
      // the new page load, so the branded loading screen appears immediately.
      window.location.href = '/login';
    },
    onError: (error: any) => {
      console.error('[AUTH_LOGOUT] AuthenticatedApp: Sign out failed:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to logout',
        variant: 'destructive',
      });
    },
  });

  // If not authenticated, show Router which handles login
  if (!isAuthenticated && !isLoading) {
    return <Router />;
  }

  // If still loading, show loading spinner
  if (isLoading) {
    return <BrandedLoadingScreen />;
  }

  // Now we know user is authenticated
  const currentUser = currentUserSafe!

  // Show different interfaces based on user roles (JSONB array)
  const userRoles = (currentUser?.roles as string[]) || [];
  const role = currentUser?.role;
  const isSuperAdmin = (Array.isArray(userRoles) && (userRoles.includes('super_admin') || userRoles.includes('superadmin')))
    || role === 'super_admin'
    || role === 'superadmin';
  const isAdmin = (Array.isArray(userRoles) && userRoles.includes('admin')) || role === 'admin';
  const isReviewer = (Array.isArray(userRoles) && userRoles.includes('reviewer')) || role === 'reviewer';
  const isPortalExternal = (currentUser as { accessMode?: string })?.accessMode === 'portal_external';

  // EMERGENCY: Grant admin access if user exists (for testing)
  const shouldShowAdminInterface = currentUser && (isAdmin || isSuperAdmin || isReviewer);

  if (isPortalExternal) {
    return (
      <div className="min-h-screen bg-background theme-mosaic">
        <Router />
      </div>
    );
  }

  dbg.log('AuthenticatedApp - User roles check:', {
    currentUser: currentUser?.username,
    userRoles,
    isSuperAdmin,
    isAdmin,
    shouldShowAdminInterface,
    userRole: currentUser?.role,
    fullCurrentUser: currentUser,
    rolesProperty: currentUser?.roles
  });

  if (currentUser && (shouldShowAdminInterface || currentUser.role === 'admin')) {
    // Admin gets full interface with sidebar
    return (
      <div className="min-h-screen bg-background theme-mosaic">
        <OfflineStatusBar />
        {/* Top Notification Bell for Admin */}
        {/* <TopNotificationBell /> */} {/* Commented out - endpoint not implemented */}

        {/* Mobile menu toggle - Enhanced for Android */}
        <div className="lg:hidden fixed top-4 left-4 z-[70]">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="bg-white shadow-md touch-manipulation min-h-[44px] min-w-[44px]"
          >
            <Menu className="h-5 w-5" />
          </Button>
        </div>

        {/* Sidebar */}
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        {/* Main content */}
        <div className="lg:ml-64 min-h-screen bg-background">
          <Router />
        </div>
      </div>
    );
  } else {
    // Operators get simplified interface with no navigation
    return (
      <MobileScaffold>
        <Router />
      </MobileScaffold>
    );
  }
});

export default App;
